import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

/**
 * Reads presence state from the React Query cache slots written by useGameSocket.
 *
 * Returns:
 *   onlineUserIds — Set<string> of userIds with at least one open WS connection.
 *   liveUserIds   — Set<string> of userIds with at least one *focused* WS connection
 *                   (AppState 'active' on native / visibilitychange visible on web).
 *
 * The underlying arrays are written by useGameSocket whenever the server pushes a
 * PRESENCE_UPDATE event. This hook subscribes to the same cache keys so it
 * re-renders automatically on every update.
 *
 * Stable Set identity per change so callers can use the sets safely in deps.
 *
 * For backward compat: the default export is the Set<string> of ONLINE users
 * (same as before) so existing call sites that do `const presence = useGamePresence(id)`
 * continue to work without changes. The new live set is an additional named export.
 */
export function useGamePresence(gameId: string | undefined): Set<string> {
  const { onlineUserIds } = useGamePresenceFull(gameId)
  return onlineUserIds
}

export interface GamePresenceResult {
  /** userIds with at least one open WebSocket to the game. */
  onlineUserIds: Set<string>
  /** userIds with at least one *focused* WebSocket (FOCUS sent, no BLUR). */
  liveUserIds: Set<string>
}

/**
 * Extended hook that exposes both the online set and the focused-live set.
 * Use this when you need to distinguish between connected-but-backgrounded
 * and actively-watching players.
 */
export function useGamePresenceFull(gameId: string | undefined): GamePresenceResult {
  // Subscribe to the online presence cache slot.
  const { data: onlineData } = useQuery<string[]>({
    queryKey: ['games', gameId, 'presence'],
    queryFn: () => Promise.resolve([]),
    enabled: !!gameId,
    staleTime: Infinity,
  })

  // Subscribe to the live (focused) presence cache slot.
  const { data: liveData } = useQuery<string[]>({
    queryKey: ['games', gameId, 'presence-live'],
    queryFn: () => Promise.resolve([]),
    enabled: !!gameId,
    staleTime: Infinity,
  })

  const rawOnline = onlineData ?? []
  const rawLive = liveData ?? []

  // Only create new Sets when the array references change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onlineUserIds = useMemo(() => new Set(rawOnline), [rawOnline])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveUserIds = useMemo(() => new Set(rawLive), [rawLive])

  return { onlineUserIds, liveUserIds }
}
